import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../utils/api';
import { 
  createAgentMessage, 
  createUserMessage, 
  createTourMessage, 
  createErrorMessage, 
  createSystemMessage, 
  createChoiceCardSuccessMessage, 
  createMockResponseMessage,
  createMessageFromDB
} from '../components2/chat/utils/messageCreators';

const ChatContext = createContext();

export { ChatContext };

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  // 🎯 SIMPLIFIED STATE: Only UI-level runtime state now
  // Backend-driven state comes from ChatStateContext (backendChatState)
  
  // Message runtime state (appended during active conversation)
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  
  // Property change state (for UI transitions)
  const [currentPropertyUrl, setCurrentPropertyUrl] = useState(null);
  const [isChangingProperty, setIsChangingProperty] = useState(false);
  const [propertyChangeState, setPropertyChangeState] = useState('idle');
  
  // Tour UI state (for showing tour controls)
  const [tourInProgress, setTourInProgress] = useState(false);
  
  // Load conversation history from database
  const loadConversationHistory = useCallback(async (propertyUrl = null) => {
    try {
      const url = propertyUrl 
        ? `/api/agent/history/?property=${encodeURIComponent(propertyUrl)}`
        : '/api/agent/history/';
      
      const response = await apiRequest(url);
      if (response.ok) {
        const data = await response.json();
        // console.log('📚 [ChatContext] History API response:', data);
        if (data.messages && data.messages.length > 0) {
          // Convert DB messages to frontend format
          const formattedMessages = data.messages.map(msg => createMessageFromDB(msg));
          setMessages(formattedMessages);
          // console.log('📚 [ChatContext] Loaded conversation history:', formattedMessages.length, 'messages');
          return true;
        } else {
          // console.log('📚 [ChatContext] No messages in history - will show property welcome message');
        }
      } else {
        // console.log('📚 [ChatContext] History API failed:', response.status);
      }
      return false;
    } catch (error) {
      console.error('❌ [ChatContext] Error loading conversation history:', error);
      return false;
    }
  }, []);

  // Start new conversation
  const handleStartNewConversation = useCallback(async () => {
    try {
      const response = await apiRequest('/api/agent/start-new-conversation/', {
        method: 'POST'
      });
      
      if (response.ok) {
        // console.log('🆕 [ChatContext] New conversation started successfully');
        // Reset to default welcome message
        setMessages([createAgentMessage(
          "👋 **Welcome to your SEO Assistant!**\n\nI'm here to help you understand your SEO performance and guide you to the right insights. I can:\n\n• **Analyze your traffic** and keyword performance\n• **Explain technical issues** and opportunities  \n• **Navigate you to specific reports** with smart filters\n• **Answer SEO questions** based on your data\n\n**Try asking me:**\n• \"Show me my brand keywords\"\n• \"Find keyword opportunities\" \n• \"Which pages are losing traffic?\"\n• \"Help me understand this page's performance\"\n• \"What should I work on first?\"\n\nWhat would you like to explore first?",
          { context: { type: 'welcome' } },
          0
        )]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ [ChatContext] Error starting new conversation:', error);
      return false;
    }
  }, []);

  // Show property-specific welcome message for new properties
  const showPropertyWelcomeMessage = useCallback((propertyUrl) => {
    const domainName = propertyUrl ? new URL(propertyUrl).hostname : 'this property';
    const welcomeMessage = createAgentMessage(
      `This looks like your first time analyzing this property. I can help you understand your SEO performance and identify opportunities.\n\n` +
      `**Would you like me to run an initial assessment?**\n\n` +
      `This will give you a comprehensive overview of your site's SEO health, traffic patterns, and key opportunities.`,
      {
        context: { type: 'property_welcome', propertyUrl },
        showInitialAssessmentPrompt: true,
        showInlineButtons: true,
        inlineButtons: [
          { text: 'Run Initial Assessment', action: 'run_initial_assessment', variant: 'primary' },
          { text: 'Explore on My Own', action: 'explore_alone', variant: 'secondary' }
        ]
      },
      0
    );
    
    // console.log('🏠 [ChatContext] Created property welcome message with buttons:', welcomeMessage);
    setMessages([welcomeMessage]);
  }, []);

  // 🎯 NOTE: Onboarding property change now handled by backend chat_state
  // Backend returns appropriate mode/flags, ChatSidebar derives welcome messages

  // Handle property change - simplified to just update state
  // Backend state update (via ChatStateContext) triggers message reinitialization in ChatSidebar
  const handlePropertyChange = useCallback(async (newPropertyUrl) => {
    console.log('🔄 [ChatContext] Property change initiated:', newPropertyUrl);
    
    // Prevent concurrent property changes
    if (isChangingProperty) {
      console.log('🔄 [ChatContext] Property change already in progress - skipping');
      return;
    }
    
    // Set changing state flags
    setIsChangingProperty(true);
    setPropertyChangeState('changing');
    setCurrentPropertyUrl(newPropertyUrl);
    
    // Note: Backend will return updated chat_state via useAnalysisState's switch-property endpoint
    // ChatStateContext.updateFromBackend() will be called
    // ChatSidebar will reinitialize messages from new backendChatState
    
    // Mark complete after brief delay (backend state update is async)
    setTimeout(() => {
      setPropertyChangeState('complete');
      setIsChangingProperty(false);
    }, 500);
  }, [isChangingProperty]);

  // Handle tour start
  const handleTourStart = useCallback(() => {
    setTourInProgress(true);
    console.log('🎯 [ChatContext] Tour started');
  }, []);

  // Handle tour complete
  const handleTourComplete = useCallback(() => {
    setTourInProgress(false);
    
    // 🎯 CLEAR ONBOARDING STATE: Remove session flag set during auth callback
    const wasOnboarding = sessionStorage.getItem('isOnboarding');
    if (wasOnboarding) {
      console.log('🎯 [ChatContext] Tour completed - clearing onboarding session state');
      sessionStorage.removeItem('isOnboarding');
      
      // Dispatch event so App.jsx can update its state
      window.dispatchEvent(new CustomEvent('onboarding-completed'));
    }
    
    console.log('🎯 [ChatContext] Tour completed');
  }, []);

  // Handle tour stop
  const handleTourStop = useCallback(() => {
    setTourInProgress(false);
    
    // 🎯 CLEAR ONBOARDING STATE: Remove session flag (tour can be stopped OR completed)
    const wasOnboarding = sessionStorage.getItem('isOnboarding');
    if (wasOnboarding) {
      console.log('🎯 [ChatContext] Tour stopped/completed - clearing onboarding session state');
      sessionStorage.removeItem('isOnboarding');
      
      // Dispatch event so App.jsx can update its state
      window.dispatchEvent(new CustomEvent('onboarding-completed'));
    }
    
    console.log('🎯 [ChatContext] Tour stopped');
  }, []);

  // 🎯 NOTE: Message initialization now handled by ChatSidebar.jsx
  // Messages are derived from backendChatState (via ChatStateContext)
  // This eliminates complex sessionStorage logic and timing issues

  const value = {
    // Runtime State (UI-level only)
    messages,
    isTyping,
    currentPropertyUrl,
    isChangingProperty,
    propertyChangeState,
    tourInProgress,
    
    // Actions
    setMessages,
    setIsTyping,
    setCurrentPropertyUrl,
    setTourInProgress,
    handlePropertyChange,
    handleStartNewConversation,
    handleTourStart,
    handleTourComplete,
    handleTourStop,
    // 🎯 DEPRECATED: Kept for backward compatibility - will be removed
    loadConversationHistory, // Only used for portal refresh
    showPropertyWelcomeMessage, // No longer needed
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
